import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Select,
  Button,
  Checkbox,
  ImageEditor,
} from "components/ui";
import { usersService } from "services/users.service";
import { useToast } from "hooks/useToast";
import { ROUTES, VALIDATION_PATTERNS } from "constants/index";
import { User } from "types/user";
import { stripDataUrl } from "../../helpers/useConvertFileToBase64";
import { useFieldValidation } from "hooks/useFieldValidation";

type UserValidationState = {
  name: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  shouldValidatePassword: boolean;
};

const createAvatarDataUrl = (base64Data: string): string => {
  if (!base64Data) return "";

  if (base64Data.startsWith("data:")) {
    return base64Data;
  }

  return `data:image/png;base64,${base64Data}`;
};

export const UserFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && !location.pathname.endsWith("/view");
  const isView = location.pathname.endsWith("/view");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const [form, setForm] = useState<Partial<User>>({
    name: "",
    username: "",
    email: "",
    avatar: "",
  });
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [changePassword, setChangePassword] = useState<boolean>(false);

  const buildValidationState = (
    overrides?: Partial<UserValidationState>
  ): UserValidationState => ({
    name: form.name || "",
    username: form.username || "",
    email: form.email || "",
    password,
    confirmPassword,
    shouldValidatePassword: !isEdit || changePassword,
    ...overrides,
  });

  const validators = useMemo(
    () => ({
      name: (state: UserValidationState) =>
        !state.name?.trim() ? "Name is required" : undefined,
      username: (state: UserValidationState) =>
        !state.username?.trim() ? "Username is required" : undefined,
      email: (state: UserValidationState) => {
        if (!state.email?.trim()) return "Email is required";
        if (!VALIDATION_PATTERNS.EMAIL.test(state.email))
          return "Enter a valid email";
        return undefined;
      },
      password: (state: UserValidationState) => {
        if (!state.shouldValidatePassword) return undefined;
        if (!state.password?.trim()) return "Password is required";
        if (!VALIDATION_PATTERNS.PASSWORD.test(state.password))
          return "Password must be at least 8 characters with uppercase, lowercase, number and special character";
        return undefined;
      },
      confirmPassword: (state: UserValidationState) => {
        if (!state.shouldValidatePassword) return undefined;
        if (!state.confirmPassword?.trim())
          return "Confirm your password";
        if (state.password !== state.confirmPassword)
          return "Passwords do not match";
        return undefined;
      },
    }),
    []
  );

  const {
    errors,
    validateAll,
    revalidateFields,
    clearFieldError,
    resetErrors,
  } = useFieldValidation<UserValidationState>(validators);

  const validationFieldMap: Record<string, keyof UserValidationState> = {
    name: "name",
    username: "username",
    email: "email",
  };

  useEffect(() => {
    const load = async () => {
      if (!(isEdit || isView) || !id) return;
      setIsLoading(true);
      setError(null);
      try {
        const u = await usersService.getById(id);
        setForm({
          name: u.name,
          username: u.username,
          email: u.email,
          avatar: createAvatarDataUrl(u.avatar || ""),
        });
        setChangePassword(false);
        setPassword("");
        setConfirmPassword("");
        resetErrors();
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e.message ?? friendly);
        toast.error(e.message ?? friendly);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isEdit, resetErrors]);

  useEffect(() => {
    // Role removed
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    const validationKey = validationFieldMap[name];
    if (validationKey) {
      const overrides: Partial<UserValidationState> = {
        [validationKey]: value,
      } as Partial<UserValidationState>;
      revalidateFields(buildValidationState(overrides), [validationKey]);
    }
  };

  const handleImageChange = (imageData: string | null) => {
    setForm((prev) => ({ ...prev, avatar: imageData || "" }));
  };

  const handleChangePasswordToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChangePassword(e.target.checked);
    if (!e.target.checked) {
      setPassword("");
      setConfirmPassword("");
      clearFieldError("password");
      clearFieldError("confirmPassword");
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    revalidateFields(buildValidationState({ password: value }), [
      "password",
      "confirmPassword",
    ]);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    revalidateFields(buildValidationState({ confirmPassword: value }), [
      "password",
      "confirmPassword",
    ]);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const validationResult = validateAll(buildValidationState());
      if (Object.keys(validationResult).length > 0) {
        setIsLoading(false);
        return;
      }

      const imageBase64 =
        typeof form.avatar === "string" && form.avatar.startsWith("data:")
          ? stripDataUrl(form.avatar)
          : form.avatar || undefined;

      const payload = {
        name: form.name,
        email: form.email,
        username: form.username,
        avatar: imageBase64,
      } as Partial<User>;

      const shouldSendPassword = !isEdit || (isEdit && changePassword);
      const payloadWithPassword = shouldSendPassword
        ? { ...(payload as any), password }
        : payload;

      if (isEdit && id) {
        await usersService.update(id, payloadWithPassword as any);
        toast.success("User updated successfully");
      } else {
        await usersService.create(payloadWithPassword as any);
        toast.success("User created successfully");
      }
      navigate(ROUTES.USERS);
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      setError(e.message ?? friendly);
      toast.error(e.message ?? friendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader
          title={isEdit ? "Edit User" : isView ? "View User" : "New User"}
        />
        <CardBody>
          <Grid cols={2} gap="lg">
            <GridItem>
              <Input
                label="Name"
                name="name"
                value={form.name || ""}
                onChange={handleChange}
                fullWidth
                error={errors.name}
                required
                disabled={isView}
              />
            </GridItem>
            <GridItem>
              <Input
                label="Username"
                name="username"
                value={form.username || ""}
                onChange={handleChange}
                fullWidth
                error={errors.username}
                required
                disabled={isView || isEdit}
              />
            </GridItem>
            <GridItem>
              <Input
                label="Email"
                name="email"
                type="email"
                value={form.email || ""}
                onChange={handleChange}
                fullWidth
                error={errors.email}
                required
                disabled={isView}
              />
            </GridItem>

            {/* <GridItem colSpan="full">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex justify-center">
                <ImageEditor
                  currentImage={form.avatar}
                  onImageChange={handleImageChange}
                  disabled={isView}
                  className="max-w-md"
                />
              </div>
            </div>
          </GridItem> */}

            {isEdit ? (
              <GridItem colSpan="full">
                {!isView && (
                  <div className="h-full flex items-center">
                    <Checkbox
                      label="Change password"
                      name="changePassword"
                      checked={changePassword}
                      onChange={handleChangePasswordToggle}
                    />
                  </div>
                )}
              </GridItem>
            ) : null}
            {(!isEdit || changePassword) && (
              <>
                <GridItem>
                  <Input
                    label="Password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    fullWidth
                    error={errors.password}
                    required
                    disabled={isView}
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    fullWidth
                    error={errors.confirmPassword}
                    required
                    disabled={isView}
                  />
                </GridItem>
              </>
            )}
          </Grid>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.USERS)}
              disabled={isLoading}
            >
              Back
            </Button>
            {!isView && (
              <Button
                variant="primary"
                onClick={onSubmit}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Save
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </MainLayout>
  );
};

export default UserFormPage;
